import * as React from 'react';
import { renderEditField } from './InputValueEditor';
import { Button } from '@/components/ui/button';
import type { DataValue, ObjectDataType, UserDefinedType } from '@glueapp/graph-types';
import { getDefaultValueForDataType } from '@glueapp/graph-types';
import { X } from 'lucide-react';

interface TypeLiteralValueEditorProps {
    type: ObjectDataType;
    value: { [key: string]: DataValue };
    userDefinedType?: UserDefinedType;
    onChange: (value: { [key: string]: DataValue }, updatedType?: UserDefinedType) => void;
}

export const ObjectValueEditor: React.FC<TypeLiteralValueEditorProps> = ({ type, value, userDefinedType, onChange }) => {
    const propertyEntries = Object.entries(type.properties);

    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-foreground">Object</label>
            <div className="border-l-2 border-primary/40 pl-3 space-y-3">
                {propertyEntries.map(([propertyKey, propertyType]) => {
                    const propertyFromValue: DataValue | undefined = value[propertyKey];
                    const isOptional = type.optionalProperties?.includes(propertyKey);
                    return (
                        <div key={propertyKey}>
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold text-foreground">{propertyKey}</span>
                                {isOptional && propertyFromValue !== undefined && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-5 w-5 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                        onClick={() => {
                                            const valueWithoutProperty = { ...value };
                                            delete valueWithoutProperty[propertyKey];
                                            onChange(valueWithoutProperty);
                                        }}
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                )}
                            </div>
                            {isOptional && propertyFromValue === undefined ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full"
                                    onClick={() => onChange({ ...value, [propertyKey]: getDefaultValueForDataType(propertyType) })}
                                >
                                    Set value
                                </Button>
                            ) : (
                                renderEditField(
                                    propertyType,
                                    propertyFromValue !== undefined ? propertyFromValue : getDefaultValueForDataType(propertyType),
                                    userDefinedType,
                                    (changedValue: DataValue) => onChange({ ...value, [propertyKey]: changedValue }),
                                )
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
